import type { LabelStrikeMapping } from '@communities/content-management/domain';
import { useStrikeConfiguration } from '@communities/content-management/hooks';
import { DataList, type TableColumn, SectionHeader } from '@core/components';
import type { DomainError } from '@core/domain';
import { Badge, BadgeVariant, Section, Text, Button } from '@soteria/design-system-ve';
import { useStandardLabels } from '@system-config/label-management/hooks';
import { useState, useCallback, useMemo } from 'react';

import { LabelMappingsDialog } from './LabelMappingsDialog';
import * as styles from './StrikeConfigSection.css';

/** Enriched mapping with resolved name and unique id for DataList */
interface LabelMappingRow {
  id: string;
  name: string;
  labelId: string;
  severity: number;
  points: number;
}

interface StrikeLabelScoringSectionProps {
  communityId: string;
  isEntityAdmin: boolean;
  /** Optional custom section title. Defaults to "Label Mappings" */
  sectionTitle?: string;
}

/**
 * Section for strike label scoring (label mappings).
 * Defines point values for each label and severity level.
 */
export function StrikeLabelScoringSection({ communityId, isEntityAdmin, sectionTitle = 'Label Mappings' }: StrikeLabelScoringSectionProps) {
  const { configuration, loading, errors, updateConfiguration } = useStrikeConfiguration(communityId);
  const { labels: standardLabels } = useStandardLabels();

  const [isLabelMappingsDialogOpen, setIsLabelMappingsDialogOpen] = useState(false);
  const [isDeletingMapping, setIsDeletingMapping] = useState(false);
  const [deleteErrors, setDeleteErrors] = useState<DomainError[] | null>(null);

  // Build label name lookup for display
  const labelNameMap = useMemo(() => {
    const map = new Map<string, string>();

    standardLabels.forEach(l => map.set(l.id, l.labelName));

    return map;
  }, [standardLabels]);

  const getLabelName = useCallback((labelId: string): string => labelNameMap.get(labelId) ?? labelId, [labelNameMap]);

  // Enrich mappings for DataList display
  const mappingRows: LabelMappingRow[] = useMemo(
    () =>
      configuration.labelStrikeMappings.map((m: LabelStrikeMapping) => ({
        id: `${m.labelId}-${m.severity}`,
        name: getLabelName(m.labelId),
        labelId: m.labelId,
        severity: m.severity,
        points: m.points,
      })),
    [configuration.labelStrikeMappings, getLabelName]
  );

  // DataList columns for label mappings
  const mappingColumns: TableColumn<LabelMappingRow>[] = useMemo(
    () => [
      {
        key: 'label',
        header: 'LABEL',
        render: (item: LabelMappingRow) => <Text className={styles.mappingLabel}>{item.name}</Text>,
        width: '40%',
      },
      {
        key: 'severity',
        header: 'SEVERITY',
        render: (item: LabelMappingRow) => <Badge text={`${item.severity}`} variant={BadgeVariant.Default} />,
        width: '25%',
      },
      {
        key: 'points',
        header: 'POINTS',
        render: (item: LabelMappingRow) => <Text className={styles.mappingLabel}>{item.points}</Text>,
        width: '25%',
      },
    ],
    []
  );

  // Handle deleting a single label mapping row
  const handleDeleteMapping = useCallback(
    async (row: LabelMappingRow): Promise<boolean> => {
      setIsDeletingMapping(true);
      setDeleteErrors(null);

      const updatedMappings = configuration.labelStrikeMappings.filter(m => !(m.labelId === row.labelId && m.severity === row.severity));

      const success = await updateConfiguration({
        rollingWindowDays: configuration.rollingWindowDays,
        labelStrikeMappings: updatedMappings,
      });

      setIsDeletingMapping(false);

      if (!success && errors) {
        setDeleteErrors(errors);
      }

      return success;
    },
    [configuration, updateConfiguration, errors]
  );

  const handleSaveLabelMappings = useCallback(
    async (mappings: LabelStrikeMapping[]): Promise<boolean> =>
      await updateConfiguration({
        rollingWindowDays: configuration.rollingWindowDays,
        labelStrikeMappings: mappings,
      }),
    [updateConfiguration, configuration.rollingWindowDays]
  );

  return (
    <>
      {/* Label Mappings Section - DataList table format */}
      <Section spacing="large">
        <SectionHeader
          description="Define point values for each label and severity level."
          endComponent={isEntityAdmin ? <Button onClick={() => setIsLabelMappingsDialogOpen(true)}>EDIT MAPPINGS</Button> : undefined}
          title={`${sectionTitle} (${configuration.labelStrikeMappings.length})`}
        />
        <DataList<LabelMappingRow>
          columns={mappingColumns}
          data={mappingRows}
          dataTypeName="label mapping"
          deleteItemContext={
            isEntityAdmin
              ? {
                  handler: handleDeleteMapping,
                  isDeleting: isDeletingMapping,
                  errors: deleteErrors,
                  resetErrors: () => setDeleteErrors(null),
                  getItemDisplayName: (item: LabelMappingRow) => `${item.name} (Severity ${item.severity})`,
                }
              : undefined
          }
          emptyStateMessage="No label mappings configured. Add labels to define strike point values."
          error={errors}
          isLoading={loading}
          onEdit={isEntityAdmin ? () => setIsLabelMappingsDialogOpen(true) : undefined}
        />
      </Section>

      {/* Label Mappings Edit Dialog */}
      <LabelMappingsDialog
        communityId={communityId}
        currentMappings={configuration.labelStrikeMappings}
        errors={errors}
        isOpen={isLabelMappingsDialogOpen}
        onClose={() => setIsLabelMappingsDialogOpen(false)}
        onSave={handleSaveLabelMappings}
      />
    </>
  );
}
